import { type Courses } from "@common/components/Courses/types";
import Button, { type ButtonProps } from "@common/controls/Button/component";
import Icon from "@common/controls/Icon/component";
import { type ModalProps } from "@common/controls/Modal/component";
import { IconType } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { i18nFormat } from "@common/lib/i18n";
import { joinDefined } from "@common/lib/misc";
import React from "react";
import { getAttemptsLeftMessage, getDelayMessage, getNextAttemptTooltip } from "./lib";
import { NewQuestion } from "./Question";
import Review from "./Review";

export type MapProps = {
  lesson: Courses.CourseLessonAppState;
  infoDisplayMode?: "oneline" | "multiline";

  ButtonComponent?: React.ComponentType<ButtonProps>;
  ConfirmModalComponent?: React.ComponentType<ModalProps>;
};

export type ActionProps = {
  answer: (questionId: string, answerIds: string[]) => void;
  back: () => void;
  reviewEnd: () => void;
  review: () => void;
  start: () => void;
};

export default (props: ActionProps & MapProps) => {
  const i18n = useI18n();

  const ButtonComponent = props.ButtonComponent ?? Button;

  const [attemptsLeftMessage, delayMessage] = React.useMemo(
    () => [getAttemptsLeftMessage(i18n, props.lesson), getDelayMessage(i18n, props.lesson)],
    [i18n, props.lesson]
  );

  if (props.lesson.quizReview) {
    return <Review questions={props.lesson.quizReview.questions} end={props.reviewEnd} />;
  }

  if (!props.lesson.quizState) {
    return (
      <div className="quiz start">
        <p className="hint">
          {props.lesson.lesson.quizQuestionCount}&nbsp;
          {props.lesson.lesson.quizQuestionCount === 1 ? i18n.question.toLowerCase() : i18n.questions.toLowerCase()}
        </p>
        <p className="header">
          <Icon className="with-skin-color" icon={IconType.Puzzle} /> {props.lesson.lesson.title}
        </p>
        <p className="info">
          {props.lesson.lesson.quizQuestionCount ? (
            (props.infoDisplayMode ?? "oneline") === "oneline" ? (
              joinDefined(" ", attemptsLeftMessage, delayMessage)
            ) : (
              <>
                {attemptsLeftMessage && <p>{attemptsLeftMessage}</p>}
                {delayMessage && <p>{delayMessage}</p>}
              </>
            )
          ) : (
            i18n.quizNoQuestionsInformation
          )}
        </p>

        <div className="actions start">
          <ButtonComponent
            isDisabled={props.lesson.status === "busy" || !props.lesson.lesson.quizQuestionCount}
            onClick={props.start}
          >
            {i18n.startQuiz}
          </ButtonComponent>
        </div>
      </div>
    );
  }

  const quizState = props.lesson.quizState;
  if (quizState.question) {
    return (
      <div className="quiz new">
        <p className="header">
          {i18nFormat(i18n.questionMOfN, ["M", quizState.question.index + 1], ["N", quizState.questionCount])}
        </p>
        <NewQuestion
          ConfirmModalComponent={props.ConfirmModalComponent}
          answer={props.answer}
          back={props.back}
          isBusy={props.lesson.status === "busy"}
          state={quizState}
        />
      </div>
    );
  }

  const grade = quizState.correct! / quizState.questionCount!;
  const isPassed = grade >= quizState.passingGrade;
  return (
    <div className="quiz result">
      <div className="header">
        <Icon
          className={isPassed ? "success" : "failure"}
          icon={isPassed ? IconType.TickCircle : IconType.CrossCircleSolid}
        />
        {i18nFormat(i18n.youScoredMOfN, ["M", quizState.correct], ["N", quizState.questionCount])} (
        {`${Math.round(grade * 100)}%`})
      </div>

      <p>
        {isPassed
          ? i18n.infoQuizSuccess
          : i18nFormat(i18n.infoQuizFail, ["GRADE", `${Math.round(quizState.passingGrade * 100)}%`])}
      </p>
      {!isPassed && (
        <React.Fragment>
          <p>{getAttemptsLeftMessage(i18n, props.lesson)}</p>
          <p>{getDelayMessage(i18n, props.lesson)}</p>
        </React.Fragment>
      )}

      <div className="actions result">
        <ButtonComponent
          tooltip={getNextAttemptTooltip(i18n, quizState.nextAttemptOnDate)}
          isDisabled={!!getNextAttemptTooltip(i18n, quizState.nextAttemptOnDate)}
          onClick={props.start}
        >
          {i18n.retakeQuiz}
        </ButtonComponent>
        <ButtonComponent type="white" onClick={props.review}>
          {i18n.reviewAnswers}
        </ButtonComponent>
      </div>
    </div>
  );
};
